// Minimal SVG import: turns pasted SVG markup into editable vector layers inside a frame.
import { parseColor, type NodeSpec } from '@vellum/core';
import { createFromSpec } from '@vellum/core';
import type { App } from './app.ts';

function paint(v: string | null, fallback: string | null): string | null {
  const c = v ?? fallback;
  if (!c || c === 'none' || c.startsWith('url(')) return null;
  try {
    parseColor(c === 'currentColor' ? '#000000' : c);
    return c === 'currentColor' ? '#000000' : c;
  } catch {
    return null;
  }
}

/** Import SVG markup as editable layers in a frame; returns the frame's id. */
export function importSvg(app: App, markup: string, at?: { x: number; y: number }): string | null {
  const doc = new DOMParser().parseFromString(markup, 'image/svg+xml');
  const svg = doc.querySelector('svg');
  if (!svg) return null;
  const vb = (svg.getAttribute('viewBox') ?? '').split(/[ ,]+/).map(Number);
  const w = parseFloat(svg.getAttribute('width') ?? '') || vb[2] || 100;
  const h = parseFloat(svg.getAttribute('height') ?? '') || vb[3] || 100;
  const sx = vb.length === 4 && vb[2] ? w / vb[2] : 1;
  const sy = vb.length === 4 && vb[3] ? h / vb[3] : 1;
  const ox = vb.length === 4 ? vb[0] : 0,
    oy = vb.length === 4 ? vb[1] : 0;
  const children: NodeSpec[] = [];
  // Measure geometry with the browser (handles transforms) by mounting the SVG off-screen.
  const host = document.createElement('div');
  host.style.cssText = 'position:fixed;left:-10000px;top:0;visibility:hidden';
  host.innerHTML = markup;
  document.body.appendChild(host);
  const live = host.querySelector('svg')!;
  const shapes = live.querySelectorAll('path,rect,circle,ellipse,polygon,polyline,line');
  shapes.forEach((el, i) => {
    const g = el as SVGGeometryElement;
    const cs = getComputedStyle(g);
    const fill = paint(g.getAttribute('fill'), cs.fill === 'none' ? null : cs.fill);
    const stroke = paint(g.getAttribute('stroke'), cs.stroke === 'none' ? null : cs.stroke);
    let d = '';
    const len = g.getTotalLength?.() ?? 0;
    if (el.tagName === 'path') d = el.getAttribute('d') ?? '';
    else if (len > 0) {
      // sample other primitives into a polyline path
      const n = Math.min(200, Math.max(8, Math.ceil(len / 4)));
      const pts = Array.from({ length: n + 1 }, (_, k) => g.getPointAtLength((len * k) / n));
      d = pts.map((p, k) => `${k ? 'L' : 'M'}${p.x} ${p.y}`).join('') + (['polyline', 'line'].includes(el.tagName) ? '' : 'Z');
    }
    if (!d) return;
    const bb = g.getBBox();
    // translate path into local coords of its bbox, then scale by viewBox
    const local = transformD(d, -bb.x, -bb.y, sx, sy);
    children.push({
      type: 'vector',
      name: el.getAttribute('id') ?? `${el.tagName} ${i + 1}`,
      x: (bb.x - ox) * sx,
      y: (bb.y - oy) * sy,
      width: Math.max(0.01, bb.width * sx),
      height: Math.max(0.01, bb.height * sy),
      path: local,
      fill: fill ?? null,
      stroke: stroke ?? undefined,
      strokeWidth: stroke ? parseFloat(g.getAttribute('stroke-width') ?? cs.strokeWidth) * sx || 1 : undefined,
      strokeAlign: 'CENTER',
      props: { windingRule: (g.getAttribute('fill-rule') ?? cs.fillRule) === 'evenodd' ? 'EVENODD' : 'NONZERO' },
    });
  });
  host.remove();
  const c = at ?? app.screenToPage({ x: app.viewportSize.width / 2, y: app.viewportSize.height / 2 });
  const res = createFromSpec(app.editor, [{ type: 'frame', name: 'SVG', x: c.x - w / 2, y: c.y - h / 2, width: w, height: h, fill: null, clip: false, children }]);
  app.editor.setSelection(res.ids);
  return res.ids[0] ?? null;
}

/** Translate then scale every coordinate pair of an absolute or relative path. */
function transformD(d: string, tx: number, ty: number, sx: number, sy: number): string {
  // Normalize via the core parser (absolute commands only), then re-emit.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  return d && sx ? coreTransform(d, tx, ty, sx, sy) : d;
}

import { parseSvgPath, transformPath, pathToSvg } from '@vellum/core';
function coreTransform(d: string, tx: number, ty: number, sx: number, sy: number) {
  return pathToSvg(transformPath(parseSvgPath(d), [sx, 0, 0, sy, tx * sx, ty * sy]));
}
